import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import ConfirmationModal from "src/components/ConfirmationModal";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor } from "@progress/kendo-data-query";
import ReactDOM from "react-dom";

// --- Interfaces ---

interface SimfarmDto {
  id: number;
  phoneNumber: string;
  carrier: string;
  active: boolean;
  isVirtual: boolean;
  reservedForWarmup: boolean;
  location: string;
  priorityLevel: number;
  notes: string | null;
  imei: string | null;
  moveAllMessagesTo: string | null;
  lastCalled: string | null;
  lastPlacedCallToDevice: string | null;
  missedCalls: number | null;
  callsInLastHour: number | null;
  callsInLast24Hours: number | null;
  connectedCallsInLastHour: number | null;
  connectedCallsInLast24Hours: number | null;
  messagesInQueue: number | null;
  messagesInHighPriorityQueue: number | null;
  blackScreenshotsInLastHour: number | null;
  blackScreenshotsInLast24Hrs: number | null;
  spamRateOverLast48Hours: number | null;
}

interface CreateSimfarmRequest {
  phoneNumber: string;
  carrier: string;
  location: string;
  priorityLevel: number;
  active: boolean;
  isVirtual: boolean;
  reservedForWarmup: boolean;
  notes: string | null;
}

interface UpdateSimfarmRequest {
  active: boolean;
  location: string;
  priorityLevel: number;
  isVirtual: boolean;
  reservedForWarmup: boolean;
  moveAllMessagesTo: string | null;
  imei: string | null;
  notes: string | null;
}

interface ScreenshotDto {
  screenshotId: number;
  urlToImage: string | null;
  fullPhone: string | null;
  carrierCalledFrom: string | null;
  flaggedAsSpam: boolean;
  isExcluded: boolean | null;
  ocrTextOfCall: string | null;
  dateOfStartOfCall: string | null;
  addedOn: string | null;
}

// --- Helpers ---

const formatPhone = (phone: string): string => {
  if (!phone || phone.length !== 10) return phone;
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
};

const timeAgo = (dateString: string | null): string => {
  if (!dateString) return "Never";
  const now = new Date();
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Never";
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return "0s ago";
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const getCarrierBadgeStyle = (carrier: string): React.CSSProperties => {
  switch (carrier) {
    case "Verizon":
      return { backgroundColor: "#cd040b", color: "#fff" };
    case "ATT":
      return { backgroundColor: "#0d6efd", color: "#fff" };
    case "TMobile":
      return { backgroundColor: "#d63384", color: "#fff" };
    default:
      return { backgroundColor: "#6c757d", color: "#fff" };
  }
};

const isStale = (lastCalled: string | null): boolean => {
  if (!lastCalled) return true;
  const date = new Date(lastCalled);
  if (isNaN(date.getTime())) return true;
  const hoursAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60);
  return hoursAgo > 12;
};

// --- Column Definitions ---

interface ColumnDef {
  field: string;
  title: string;
  defaultVisible: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { field: "phoneNumber", title: "Phone #", defaultVisible: true },
  { field: "carrier", title: "Carrier", defaultVisible: true },
  { field: "active", title: "Active", defaultVisible: true },
  { field: "location", title: "Location", defaultVisible: true },
  { field: "priorityLevel", title: "Priority", defaultVisible: true },
  { field: "isVirtual", title: "Virtual", defaultVisible: true },
  { field: "reservedForWarmup", title: "Warmup", defaultVisible: true },
  { field: "lastCalled", title: "Last Screenshot", defaultVisible: true },
  { field: "missedCalls", title: "Missed", defaultVisible: true },
  { field: "callsInLastHour", title: "Calls/Hr", defaultVisible: true },
  { field: "callsInLast24Hours", title: "Calls/24h", defaultVisible: false },
  {
    field: "connectedCallsInLastHour",
    title: "Connected/Hr",
    defaultVisible: false,
  },
  {
    field: "connectedCallsInLast24Hours",
    title: "Connected/24h",
    defaultVisible: false,
  },
  { field: "messagesInQueue", title: "Queue", defaultVisible: true },
  {
    field: "blackScreenshotsInLastHour",
    title: "Invalid SS/Hr",
    defaultVisible: true,
  },
  {
    field: "blackScreenshotsInLast24Hrs",
    title: "Invalid SS/24h",
    defaultVisible: false,
  },
  {
    field: "lastPlacedCallToDevice",
    title: "Last Dial Attempt",
    defaultVisible: false,
  },
  {
    field: "spamRateOverLast48Hours",
    title: "Spam Rate (48h)",
    defaultVisible: false,
  },
];

const STORAGE_KEY = "simfarm-grid-columns";

const loadColumnVisibility = (): Set<string> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch {
    /* ignore */
  }
  return new Set(
    ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.field),
  );
};

// --- Loading Panel ---

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

// --- Component ---

export default function SimFarmGrid() {
  const navigate = useNavigate();

  // Grid state
  const [data, setData] = useState<SimfarmDto[]>([]);
  const [total, setTotal] = useState(0);
  const [locations, setLocations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState({ skip: 0, take: 25 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "phoneNumber", dir: "asc" },
  ]);
  const [refreshKey, setRefreshKey] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] =
    useState<Set<string>>(loadColumnVisibility);
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const columnChooserRef = useRef<HTMLDivElement>(null);

  const toggleColumn = useCallback((field: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Close column chooser on outside click
  useEffect(() => {
    if (!showColumnChooser) return;
    const handleClick = (e: MouseEvent) => {
      if (
        columnChooserRef.current &&
        !columnChooserRef.current.contains(e.target as Node)
      ) {
        setShowColumnChooser(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showColumnChooser]);

  // Filter state — default to Active, Non-Virtual
  const [filterCarrier, setFilterCarrier] = useState("");
  const [filterActive, setFilterActive] = useState("Active");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterWarmup, setFilterWarmup] = useState("");
  const [filterVirtual, setFilterVirtual] = useState("Physical");
  const [filterSearch, setFilterSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce phone search — waits 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterSearch), 400);
    return () => clearTimeout(timer);
  }, [filterSearch]);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDevice, setEditDevice] = useState<SimfarmDto | null>(null);
  const [editTab, setEditTab] = useState<"settings" | "screenshots">(
    "settings",
  );
  const [screenshots, setScreenshots] = useState<ScreenshotDto[]>([]);
  const [screenshotsHasMore, setScreenshotsHasMore] = useState(false);
  const [screenshotsLoading, setScreenshotsLoading] = useState(false);
  const [screenshotFilter, setScreenshotFilter] = useState("all");
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateSimfarmRequest>({
    active: true,
    location: "",
    priorityLevel: 10,
    isVirtual: false,
    reservedForWarmup: false,
    moveAllMessagesTo: null,
    imei: null,
    notes: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSimfarmRequest>({
    phoneNumber: "",
    carrier: "Verizon",
    location: "Santa Rosa",
    priorityLevel: 10,
    active: true,
    isVirtual: false,
    reservedForWarmup: false,
    notes: null,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // --- Data Loading ---

  const loadData = async () => {
    setIsLoading(true);
    try {
      const currentSort = sort.length > 0 ? sort[0] : null;
      const response = await callApi("sysadmin/simfarm/list", HttpMethod.POST, {
        skip: page.skip,
        take: page.take,
        sortField: currentSort?.field || "phoneNumber",
        sortDir: currentSort?.dir || "asc",
        carrier: filterCarrier || undefined,
        active:
          filterActive === "Active"
            ? true
            : filterActive === "Inactive"
              ? false
              : undefined,
        location: filterLocation || undefined,
        isVirtual:
          filterVirtual === "Virtual"
            ? true
            : filterVirtual === "Physical"
              ? false
              : undefined,
        reservedForWarmup:
          filterWarmup === "Warmup Only"
            ? true
            : filterWarmup === "Non-Warmup"
              ? false
              : undefined,
        search: debouncedSearch?.replace(/\D/g, "") || undefined,
      });
      setData(response.data || []);
      setTotal(response.total || 0);
      if (response.locations) setLocations(response.locations);
    } catch (err: any) {
      console.error("Error fetching sim farm devices:", err);
      toast.error(`Error fetching devices: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    loadData();
  }, [
    page,
    sort,
    refreshKey,
    filterCarrier,
    filterActive,
    filterVirtual,
    filterLocation,
    filterWarmup,
    debouncedSearch,
  ]);

  // Reset to first page when any filter changes
  useEffect(() => {
    setPage((p) => (p.skip === 0 ? p : { ...p, skip: 0 }));
  }, [
    filterCarrier,
    filterActive,
    filterVirtual,
    filterLocation,
    filterWarmup,
    debouncedSearch,
  ]);

  // --- Handlers ---

  const handleSearch = () => {
    setDebouncedSearch(filterSearch);
    setPage({ skip: 0, take: page.take });
    setRefreshKey((k) => k + 1);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleEditClick = (device: SimfarmDto) => {
    setEditDevice(device);
    setEditForm({
      active: device.active,
      location: device.location,
      priorityLevel: device.priorityLevel,
      isVirtual: device.isVirtual,
      reservedForWarmup: device.reservedForWarmup,
      moveAllMessagesTo: device.moveAllMessagesTo || null,
      imei: device.imei || null,
      notes: device.notes || null,
    });
    setEditTab("settings");
    setScreenshots([]);
    setScreenshotsHasMore(false);
    setScreenshotFilter("all");
    setShowEditModal(true);
  };

  const loadScreenshots = async (
    phone: string,
    skip: number,
    filter: string,
    append = false,
  ) => {
    setScreenshotsLoading(true);
    try {
      const response = await callApi(
        `sysadmin/simfarm/${phone}/screenshots?skip=${skip}&take=20&filter=${filter}`,
        HttpMethod.GET,
      );
      setScreenshots((prev) =>
        append ? [...prev, ...response.data] : response.data,
      );
      setScreenshotsHasMore(response.hasMore);
    } catch (err: any) {
      toast.error(`Failed to load screenshots: ${err.message || err}`);
    } finally {
      setScreenshotsLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editDevice) return;

    // Confirm if toggling active off
    if (editDevice.active && !editForm.active) {
      setShowDeactivateConfirm(true);
      return;
    }

    await saveEdit();
  };

  const saveEdit = async () => {
    if (!editDevice) return;
    setIsSaving(true);
    try {
      const response = await callApi(
        `sysadmin/simfarm/${editDevice.id}`,
        HttpMethod.PUT,
        editForm,
      );
      toast.success(response.message || "Device updated successfully");
      setShowEditModal(false);
      setEditDevice(null);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err.message || "Failed to update device");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateDevice = async () => {
    const phone = createForm.phoneNumber.replace(/\D/g, "");
    if (phone.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    if (!createForm.carrier) {
      toast.error("Carrier is required");
      return;
    }

    setIsCreating(true);
    try {
      const response = await callApi(
        "sysadmin/simfarm/create",
        HttpMethod.POST,
        { ...createForm, phoneNumber: phone },
      );
      toast.success(response.message || "Device created successfully");
      setShowCreateModal(false);
      setCreateForm({
        phoneNumber: "",
        carrier: "Verizon",
        location: "Santa Rosa",
        priorityLevel: 10,
        active: true,
        isVirtual: false,
        reservedForWarmup: false,
        notes: null,
      });
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err.message || "Failed to create device");
    } finally {
      setIsCreating(false);
    }
  };

  // --- Row styling ---

  const rowRender = (
    row: React.ReactElement<HTMLTableRowElement>,
    props: { dataItem: SimfarmDto },
  ) => {
    const device = props.dataItem;
    let style: React.CSSProperties = {};

    if (device.active && isStale(device.lastCalled)) {
      style = { backgroundColor: "#fff3f3" };
    } else if ((device.missedCalls ?? 0) > 20) {
      style = { backgroundColor: "#fffde7" };
    }

    return React.cloneElement(row, {
      style: { ...style, cursor: "pointer" },
      onClick: () => handleEditClick(device),
    } as any);
  };

  // --- Cell Renderers ---

  const PhoneCell = (props: GridCellProps) => (
    <td>{formatPhone(props.dataItem.phoneNumber)}</td>
  );

  const CarrierCell = (props: GridCellProps) => {
    const carrier = props.dataItem.carrier;
    return (
      <td>
        <span className="badge" style={getCarrierBadgeStyle(carrier)}>
          {carrier}
        </span>
      </td>
    );
  };

  const ActiveCell = (props: GridCellProps) => {
    const active = props.dataItem.active;
    return (
      <td>
        <span className={`badge ${active ? "bg-success" : "bg-danger"}`}>
          {active ? "Active" : "Inactive"}
        </span>
      </td>
    );
  };

  const VirtualCell = (props: GridCellProps) => {
    const isVirtual = props.dataItem.isVirtual;
    return (
      <td>
        {isVirtual ? (
          <span className="badge bg-info text-dark">Virtual</span>
        ) : (
          <span className="text-muted">-</span>
        )}
      </td>
    );
  };

  const WarmupCell = (props: GridCellProps) => {
    const warmup = props.dataItem.reservedForWarmup;
    return (
      <td>
        {warmup ? (
          <span className="badge bg-primary">Warmup</span>
        ) : (
          <span className="text-muted">-</span>
        )}
      </td>
    );
  };

  const LastCalledCell = (props: GridCellProps) => (
    <td>{timeAgo(props.dataItem.lastCalled)}</td>
  );

  const QueueCell = (props: GridCellProps) => {
    const d = props.dataItem;
    const total =
      (d.messagesInQueue ?? 0) + (d.messagesInHighPriorityQueue ?? 0);
    return <td>{total > 0 ? total : "-"}</td>;
  };

  const NumberCell = (props: GridCellProps) => {
    const val = props.dataItem[props.field!];
    return <td>{val != null ? val : "-"}</td>;
  };

  const LastDialCell = (props: GridCellProps) => (
    <td>{timeAgo(props.dataItem.lastPlacedCallToDevice)}</td>
  );

  const SpamRateCell = (props: GridCellProps) => {
    const val = props.dataItem.spamRateOverLast48Hours;
    return <td>{val != null ? `${(val * 100).toFixed(1)}%` : "-"}</td>;
  };

  const ActionsCell = (props: GridCellProps) => (
    <td className="text-center">
      <button
        className="btn btn-sm btn-outline-secondary"
        title="Edit"
        onClick={() => handleEditClick(props.dataItem)}
      >
        <i className="bi bi-pencil"></i>
      </button>
    </td>
  );

  // Cell renderer map for dynamic column rendering
  const columnCellMap: Record<
    string,
    React.ComponentType<GridCellProps> | undefined
  > = {
    phoneNumber: PhoneCell,
    carrier: CarrierCell,
    active: ActiveCell,
    isVirtual: VirtualCell,
    reservedForWarmup: WarmupCell,
    lastCalled: LastCalledCell,
    missedCalls: NumberCell,
    callsInLastHour: NumberCell,
    callsInLast24Hours: NumberCell,
    connectedCallsInLastHour: NumberCell,
    connectedCallsInLast24Hours: NumberCell,
    messagesInQueue: QueueCell,
    blackScreenshotsInLastHour: NumberCell,
    blackScreenshotsInLast24Hrs: NumberCell,
    lastPlacedCallToDevice: LastDialCell,
    spamRateOverLast48Hours: SpamRateCell,
  };

  // --- Render ---

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">SIM Farm Devices</h4>
          <small className="text-muted">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/sysadmin/sim-farm");
              }}
            >
              Back to Dashboard
            </a>
          </small>
        </div>
        <div className="d-flex gap-2">
          {/* Column Chooser */}
          <div className="position-relative" ref={columnChooserRef}>
            <button
              className="btn btn-primary"
              onClick={() => setShowColumnChooser((v) => !v)}
              title="Show/hide columns"
            >
              <i className="bi bi-layout-three-columns me-1"></i>
              Columns
              <span className="badge bg-white text-primary ms-1">
                {visibleColumns.size}/{ALL_COLUMNS.length}
              </span>
            </button>
            {showColumnChooser && (
              <div
                className="position-absolute end-0 mt-1 bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1050,
                  width: 280,
                  maxHeight: 400,
                  overflowY: "auto",
                }}
              >
                <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
                  <strong>Visible Columns</strong>
                  <button
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() => {
                      const defaults = new Set(
                        ALL_COLUMNS.filter((c) => c.defaultVisible).map(
                          (c) => c.field,
                        ),
                      );
                      setVisibleColumns(defaults);
                      localStorage.setItem(
                        STORAGE_KEY,
                        JSON.stringify([...defaults]),
                      );
                    }}
                  >
                    Reset
                  </button>
                </div>
                {ALL_COLUMNS.map((col) => (
                  <label
                    key={col.field}
                    className="d-flex align-items-center px-3 py-2 m-0"
                    style={{ cursor: "pointer", fontSize: "1rem" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "")
                    }
                  >
                    <input
                      type="checkbox"
                      className="form-check-input me-2 mt-0"
                      checked={visibleColumns.has(col.field)}
                      onChange={() => toggleColumn(col.field)}
                    />
                    <span>{col.title}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Add Device
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-0 small">Carrier</label>
              <select
                className="form-select form-select-sm"
                value={filterCarrier}
                onChange={(e) => setFilterCarrier(e.target.value)}
              >
                <option value="">All</option>
                <option value="Verizon">Verizon</option>
                <option value="ATT">ATT</option>
                <option value="TMobile">TMobile</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-0 small">Status</label>
              <select
                className="form-select form-select-sm"
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-0 small">Device Type</label>
              <select
                className="form-select form-select-sm"
                value={filterVirtual}
                onChange={(e) => setFilterVirtual(e.target.value)}
              >
                <option value="">All</option>
                <option value="Physical">Physical</option>
                <option value="Virtual">Virtual</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-0 small">Location</label>
              <select
                className="form-select form-select-sm"
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
              >
                <option value="">All</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-0 small">Warmup</label>
              <select
                className="form-select form-select-sm"
                value={filterWarmup}
                onChange={(e) => setFilterWarmup(e.target.value)}
              >
                <option value="">All</option>
                <option value="Warmup Only">Warmup Only</option>
                <option value="Non-Warmup">Non-Warmup</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-0 small">Phone</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search phone..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{ width: 160 }}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setFilterCarrier("");
                  setFilterActive("Active");
                  setFilterVirtual("Physical");
                  setFilterLocation("");
                  setFilterWarmup("");
                  setFilterSearch("");
                  setPage({ skip: 0, take: page.take });
                  setRefreshKey((k) => k + 1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
          {/* Active filter badges */}
          {(filterCarrier ||
            filterActive ||
            filterVirtual ||
            filterLocation ||
            filterWarmup ||
            filterSearch) && (
            <div className="d-flex flex-wrap gap-1 mt-2">
              {filterActive && (
                <span className="badge bg-success">
                  {filterActive}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterActive("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
              {filterVirtual && (
                <span className="badge bg-secondary">
                  {filterVirtual}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterVirtual("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
              {filterCarrier && (
                <span className="badge bg-primary">
                  Carrier: {filterCarrier}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterCarrier("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
              {filterLocation && (
                <span className="badge bg-info text-dark">
                  Location: {filterLocation}
                  <button
                    type="button"
                    className="btn-close ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterLocation("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
              {filterWarmup && (
                <span className="badge bg-warning text-dark">
                  {filterWarmup}
                  <button
                    type="button"
                    className="btn-close ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterWarmup("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
              {filterSearch && (
                <span className="badge bg-dark">
                  Phone: {filterSearch}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: "0.5em" }}
                    onClick={() => {
                      setFilterSearch("");
                      handleSearch();
                    }}
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={data}
          total={total}
          skip={page.skip}
          take={page.take}
          pageable={{ buttonCount: 5, pageSizes: [25, 50, 100] }}
          sortable={true}
          resizable={true}
          sort={sort}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          rowRender={rowRender}
          style={{ height: "calc(100vh - 300px)" }}
        >
          {ALL_COLUMNS.filter((c) => visibleColumns.has(c.field)).map((col) => (
            <Column
              key={col.field}
              field={col.field}
              title={col.title}
              cell={columnCellMap[col.field]}
            />
          ))}
          <Column
            title="Actions"
            width={70}
            cell={ActionsCell}
            sortable={false}
          />
        </Grid>
      </div>

      {/* Edit Modal */}
      {showEditModal && editDevice && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {formatPhone(editDevice.phoneNumber)} — {editDevice.carrier}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              {/* Tabs */}
              <ul className="nav nav-tabs px-3 pt-2">
                <li className="nav-item">
                  <button
                    className={`nav-link ${editTab === "settings" ? "active" : ""}`}
                    onClick={() => setEditTab("settings")}
                  >
                    Settings
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${editTab === "screenshots" ? "active" : ""}`}
                    onClick={() => {
                      setEditTab("screenshots");
                      if (screenshots.length === 0 && editDevice) {
                        loadScreenshots(
                          editDevice.phoneNumber,
                          0,
                          screenshotFilter,
                        );
                      }
                    }}
                  >
                    Screenshots
                    {screenshots.length > 0 && (
                      <span className="badge bg-secondary ms-1">
                        {screenshots.length}
                      </span>
                    )}
                  </button>
                </li>
              </ul>

              {/* Settings Tab */}
              {editTab === "settings" && (
                <div className="modal-body" style={{ fontSize: "1rem" }}>
                  {/* Read-only info */}
                  <div className="mb-3 p-2 bg-light rounded">
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted d-block">Phone</small>
                        <strong>{formatPhone(editDevice.phoneNumber)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Carrier</small>
                        <span
                          className="badge"
                          style={getCarrierBadgeStyle(editDevice.carrier)}
                        >
                          {editDevice.carrier}
                        </span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-6">
                        <small className="text-muted d-block">
                          Last Generated Screenshot
                        </small>
                        <span>{timeAgo(editDevice.lastCalled)}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">
                          Missed Calls
                        </small>
                        <span>{editDevice.missedCalls ?? "-"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Editable fields */}
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="editActive"
                          checked={editForm.active}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              active: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="editActive"
                        >
                          Active
                        </label>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="editVirtual"
                          checked={editForm.isVirtual}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              isVirtual: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="editVirtual"
                        >
                          Is Virtual
                        </label>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="editWarmup"
                          checked={editForm.reservedForWarmup}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              reservedForWarmup: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="editWarmup"
                        >
                          Reserved for Warmup
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-1">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.location}
                        onChange={(e) =>
                          setEditForm({ ...editForm, location: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-1">Priority Level</label>
                      <select
                        className="form-select"
                        value={editForm.priorityLevel}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            priorityLevel: parseInt(e.target.value),
                          })
                        }
                      >
                        <option value={0}>0 (Highest)</option>
                        <option value={1}>1</option>
                        <option value={10}>10 (Default)</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-1">
                        Move All Messages To
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="10-digit phone number"
                        value={editForm.moveAllMessagesTo || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            moveAllMessagesTo: e.target.value || null,
                          })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-1">IMEI</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.imei || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            imei: e.target.value || null,
                          })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-1">Notes</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editForm.notes || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            notes: e.target.value || null,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Screenshots Tab */}
              {editTab === "screenshots" && (
                <div
                  className="modal-body"
                  style={{ fontSize: "1rem", minHeight: 400 }}
                >
                  {/* Filter */}
                  <div className="d-flex gap-2 mb-3">
                    {["all", "excluded", "spam"].map((f) => (
                      <button
                        key={f}
                        className={`btn btn-sm ${screenshotFilter === f ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => {
                          setScreenshotFilter(f);
                          if (editDevice)
                            loadScreenshots(editDevice.phoneNumber, 0, f);
                        }}
                      >
                        {f === "all"
                          ? "All"
                          : f === "excluded"
                            ? "Excluded Only"
                            : "Spam Only"}
                      </button>
                    ))}
                    <span className="text-muted ms-auto align-self-center">
                      {screenshots.length} loaded
                      {screenshotsHasMore ? " (more available)" : ""}
                    </span>
                  </div>

                  {screenshotsLoading && screenshots.length === 0 && (
                    <div className="text-center py-5 text-muted">
                      Loading screenshots...
                    </div>
                  )}

                  {!screenshotsLoading && screenshots.length === 0 && (
                    <div className="text-center py-5 text-muted">
                      No screenshots found
                    </div>
                  )}

                  {/* Thumbnail Grid */}
                  <div className="row g-2">
                    {screenshots.map((ss) => (
                      <div
                        key={ss.screenshotId}
                        className="col-6 col-md-4 col-lg-3"
                      >
                        <div
                          className="card h-100"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            ss.urlToImage && setLightboxUrl(ss.urlToImage)
                          }
                        >
                          {ss.urlToImage ? (
                            <img
                              src={ss.urlToImage}
                              alt="Screenshot"
                              className="card-img-top"
                              style={{
                                maxHeight: 280,
                                objectFit: "contain",
                                backgroundColor: "#f8f9fa",
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="card-img-top bg-dark d-flex align-items-center justify-content-center"
                              style={{ height: 200 }}
                            >
                              <span className="text-muted">No image</span>
                            </div>
                          )}
                          <div className="card-body p-2">
                            <div className="d-flex gap-1 flex-wrap mb-1">
                              {ss.flaggedAsSpam && (
                                <span className="badge bg-danger">Spam</span>
                              )}
                              {!ss.flaggedAsSpam && (
                                <span className="badge bg-success">Clean</span>
                              )}
                              {ss.isExcluded && (
                                <span className="badge bg-warning text-dark">
                                  Excluded
                                </span>
                              )}
                              {ss.carrierCalledFrom && (
                                <span className="badge bg-secondary">
                                  {ss.carrierCalledFrom}
                                </span>
                              )}
                            </div>
                            {ss.fullPhone && (
                              <div className="small">
                                {formatPhone(ss.fullPhone)}
                              </div>
                            )}
                            <div className="small text-muted">
                              {ss.dateOfStartOfCall
                                ? new Date(
                                    ss.dateOfStartOfCall,
                                  ).toLocaleString()
                                : "--"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More */}
                  {screenshotsHasMore && (
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() =>
                          editDevice &&
                          loadScreenshots(
                            editDevice.phoneNumber,
                            screenshots.length,
                            screenshotFilter,
                            true,
                          )
                        }
                        disabled={screenshotsLoading}
                      >
                        {screenshotsLoading ? "Loading..." : "Load More"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Close
                </button>
                {editTab === "settings" && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Overlay */}
      {lightboxUrl && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            zIndex: 2000,
            cursor: "pointer",
          }}
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="Full screenshot"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Device</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ fontSize: "1rem" }}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label mb-1">Phone Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="10-digit phone number"
                      value={createForm.phoneNumber}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          phoneNumber: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label mb-1">Carrier *</label>
                    <select
                      className="form-select"
                      value={createForm.carrier}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          carrier: e.target.value,
                        })
                      }
                    >
                      <option value="Verizon">Verizon</option>
                      <option value="ATT">ATT</option>
                      <option value="TMobile">TMobile</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label mb-1">Location</label>
                    <input
                      type="text"
                      className="form-control"
                      value={createForm.location}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          location: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label mb-1">Priority Level</label>
                    <select
                      className="form-select"
                      value={createForm.priorityLevel}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          priorityLevel: parseInt(e.target.value),
                        })
                      }
                    >
                      <option value={0}>0 (Highest)</option>
                      <option value={1}>1</option>
                      <option value={10}>10 (Default)</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="createActive"
                        checked={createForm.active}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            active: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="createActive"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="createVirtual"
                        checked={createForm.isVirtual}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            isVirtual: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="createVirtual"
                      >
                        Is Virtual
                      </label>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="createWarmup"
                        checked={createForm.reservedForWarmup}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            reservedForWarmup: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="createWarmup"
                      >
                        Reserved for Warmup
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label mb-1">Notes</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={createForm.notes || ""}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          notes: e.target.value || null,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateDevice}
                  disabled={isCreating}
                >
                  {isCreating ? "Creating..." : "Create Device"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isVisible={showDeactivateConfirm}
        title="Deactivate Device?"
        htmlMessage={`This will mark <strong>${editDevice ? formatPhone(editDevice.phoneNumber) : ""}</strong> as inactive. It will no longer receive calls.`}
        confirmText="Yes, Deactivate"
        confirmVariant="danger"
        onConfirm={() => {
          setShowDeactivateConfirm(false);
          saveEdit();
        }}
        onCancel={() => setShowDeactivateConfirm(false)}
      />
    </div>
  );
}
